import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getWarehouseStats, getWarehouseCatalog, createCatalogItem, getWarehouseInventory, createInventoryEntry, getWarehouseRemnants, consumeRemnant, getStillages, dispatchStillage, getQRCodeUrl, uploadCuttingList, getCuttingHistory, getJobs, getLocationMaster, consumeInventory } from '../services/api';
import { toast } from 'sonner';
import { Plus, Warehouse, Package, Scissors, Truck, Scan, BarChart3, FileSpreadsheet } from 'lucide-react';

import { OverviewStats } from '../components/warehouse/OverviewStats';
import { StockChart } from '../components/warehouse/StockChart';
import { AlertsCard } from '../components/warehouse/AlertsCard';
import { InventoryTab } from '../components/warehouse/InventoryTab';
import { RemnantsTab } from '../components/warehouse/RemnantsTab';
import { DispatchTab } from '../components/warehouse/DispatchTab';
import { CatalogTab } from '../components/warehouse/CatalogTab';
import { CuttingTab } from '../components/warehouse/CuttingTab';
import { CatalogDialog, InwardDialog, QRDialog } from '../components/warehouse/WarehouseDialogs';

function WarehousePage() {
  var initialTab = 'overview';
  var initialCatalogForm = { item_code: '', item_name: '', system_type: 'ykk', category: 'profile', uom: 'meters', item_type: 'profile', finish: '', material_type: '', length_m: 0, minimum_stock_level: 100 };
  var initialInwardForm = { catalog_id: '', batch_number: '', location_id: '', warehouse_location: { warehouse: 'Main', zone: '', aisle: '', rack: '', shelf: '', bin: '' }, item_type: 'profile', finish: '', material_type: '', length_m: 0, price_per_uom: 0, gst_percent: 0, quantity: 0, color_finish: '', qc_passed: false, unit_cost: 0 };
  var initialCuttingSettings = { blade_kerf_mm: 3.0, end_trim_mm: 5.0, job_id: '' };

  var stateActiveTab = useState(initialTab);
  var activeTab = stateActiveTab[0];
  var setActiveTab = stateActiveTab[1];

  var stateStats = useState(null);
  var stats = stateStats[0];
  var setStats = stateStats[1];

  var stateCatalog = useState([]);
  var catalog = stateCatalog[0];
  var setCatalog = stateCatalog[1];

  var stateInventory = useState([]);
  var inventory = stateInventory[0];
  var setInventory = stateInventory[1];

  var stateRemnants = useState([]);
  var remnants = stateRemnants[0];
  var setRemnants = stateRemnants[1];

  var stateStillages = useState([]);
  var stillages = stateStillages[0];
  var setStillages = stateStillages[1];

  var stateJobs = useState([]);
  var jobs = stateJobs[0];
  var setJobs = stateJobs[1];

  var stateCuttingHistory = useState([]);
  var cuttingHistory = stateCuttingHistory[0];
  var setCuttingHistory = stateCuttingHistory[1];

  var stateLoading = useState(true);
  var loading = stateLoading[0];
  var setLoading = stateLoading[1];

  var stateSearch = useState('');
  var search = stateSearch[0];
  var setSearch = stateSearch[1];

  var stateSystemFilter = useState('all');
  var systemFilter = stateSystemFilter[0];
  var setSystemFilter = stateSystemFilter[1];

  var stateCatalogDialog = useState(false);
  var catalogDialogOpen = stateCatalogDialog[0];
  var setCatalogDialogOpen = stateCatalogDialog[1];

  var stateInwardDialog = useState(false);
  var inwardDialogOpen = stateInwardDialog[0];
  var setInwardDialogOpen = stateInwardDialog[1];

  var stateQRDialog = useState(false);
  var qrDialogOpen = stateQRDialog[0];
  var setQRDialogOpen = stateQRDialog[1];

  var stateSelectedQR = useState(null);
  var selectedQR = stateSelectedQR[0];
  var setSelectedQR = stateSelectedQR[1];

  var stateCatalogForm = useState(initialCatalogForm);
  var catalogForm = stateCatalogForm[0];
  var setCatalogForm = stateCatalogForm[1];

  var stateInwardForm = useState(initialInwardForm);
  var inwardForm = stateInwardForm[0];
  var setInwardForm = stateInwardForm[1];

  var stateCuttingFile = useState(null);
  var cuttingFile = stateCuttingFile[0];
  var setCuttingFile = stateCuttingFile[1];

  var stateCuttingSettings = useState(initialCuttingSettings);
  var cuttingSettings = stateCuttingSettings[0];
  var setCuttingSettings = stateCuttingSettings[1];

  var stateCuttingResults = useState(null);
  var cuttingResults = stateCuttingResults[0];
  var setCuttingResults = stateCuttingResults[1];

  var stateCuttingProcessing = useState(false);
  var cuttingProcessing = stateCuttingProcessing[0];
  var setCuttingProcessing = stateCuttingProcessing[1];

  var stateLocationMaster = useState(null);
  var locationMaster = stateLocationMaster[0];
  var setLocationMaster = stateLocationMaster[1];

  var fetchData = useCallback(function() {
    return Promise.all([
      getWarehouseStats(), 
      getWarehouseCatalog({}), 
      getWarehouseInventory({}), 
      getWarehouseRemnants({}), 
      getStillages({}),
      getJobs({}),
      getCuttingHistory({ limit: 20 }),
      getLocationMaster()
    ]).then(function(results) {
      setStats(results[0].data);
      setCatalog(results[1].data);
      setInventory(results[2].data);
      setRemnants(results[3].data);
      setStillages(results[4].data);
      setJobs(results[5].data);
      setCuttingHistory(results[6].data.history || []);
      setLocationMaster(results[7].data);
    }).catch(function() {
      toast.error('Failed to load warehouse data');
    }).finally(function() {
      setLoading(false);
    });
  }, []);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  useEffect(function() {
    if (stats && stats.low_stock_alerts && stats.low_stock_alerts.length > 0) {
      var alerts = stats.low_stock_alerts.slice(0, 2);
      alerts.forEach(function(a) {
        toast.warning('Low Stock: ' + a.item_code, { duration: 5000 });
      });
    }
  }, [stats]);

  function handleCuttingUpload() {
    if (!cuttingFile) {
      toast.error('Please select a cutting list file');
      return;
    }
    setCuttingProcessing(true);
    uploadCuttingList(cuttingFile, cuttingSettings.blade_kerf_mm, cuttingSettings.end_trim_mm, cuttingSettings.job_id || null)
      .then(function(response) {
        setCuttingResults(response.data);
        toast.success('Cutting list processed: ' + response.data.summary.total_items_processed + ' cuts completed');
        if (response.data.summary.total_remnants_created > 0) {
          toast.info(response.data.summary.total_remnants_created + ' remnants created (>1500mm)');
        }
        if (response.data.summary.items_with_insufficient_stock > 0) {
          toast.warning(response.data.summary.items_with_insufficient_stock + ' items had insufficient stock');
        }
        setCuttingFile(null);
        fetchData();
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Failed to process cutting list');
      })
      .finally(function() {
        setCuttingProcessing(false);
      });
  }

  function handleCreateCatalog(e) {
    e.preventDefault();
    if (!catalogForm.item_code || !catalogForm.item_name) {
      toast.error('Item code and name required');
      return;
    }
    createCatalogItem(catalogForm).then(function() {
      toast.success('Item added');
      setCatalogDialogOpen(false);
      setCatalogForm(initialCatalogForm);
      fetchData();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Error');
    });
  }

  function handleCreateInward(e) {
    e.preventDefault();
    var wl = inwardForm.warehouse_location || {};
    var composedLocation = [wl.warehouse, wl.zone, wl.aisle, wl.rack, wl.shelf, wl.bin].filter(Boolean).join('/') || wl.warehouse || 'Main';
    if (!inwardForm.catalog_id || !inwardForm.batch_number || inwardForm.quantity <= 0 || !wl.warehouse) {
      toast.error('Fill required fields (item, batch, warehouse, quantity)');
      return;
    }
    var payload = { ...inwardForm, location_id: composedLocation };
    createInventoryEntry(payload).then(function(res) {
      toast.success('Entry created');
      if (res.data && res.data.capacity_warning) toast.warning(res.data.capacity_warning);
      setInwardDialogOpen(false);
      setInwardForm(initialInwardForm);
      fetchData();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Error');
    });
  }

  function showQR(type, id, label) {
    setSelectedQR({ url: getQRCodeUrl(type, id), label: label });
    setQRDialogOpen(true);
  }

  function handleConsumeRemnant(remnantId) {
    consumeRemnant(remnantId).then(function() {
      toast.success('Consumed');
      fetchData();
    }).catch(function() {
      toast.error('Error');
    });
  }

  function handleDispatch(stillageId) {
    dispatchStillage(stillageId).then(function() {
      toast.success('Dispatched');
      fetchData();
    }).catch(function() {
      toast.error('Error');
    });
  }

  function handleConsumeInventory(batchId, quantity) {
    return consumeInventory(batchId, quantity).then(function(res) {
      toast.success('Consumed ' + quantity + ' — Material Library updated');
      fetchData();
      return res;
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Error');
      throw err;
    });
  }

  var filteredInventory = inventory.filter(function(item) {
    var matchSearch = (item.item_code || '').toLowerCase().includes(search.toLowerCase()) || 
                      (item.batch_id || '').toLowerCase().includes(search.toLowerCase());
    var matchSystem = systemFilter === 'all' || item.system_type === systemFilter;
    return matchSearch && matchSystem;
  });

  var pieData = [];
  if (stats) {
    if (stats.ykk_item_count > 0) pieData.push({ name: 'YKK', value: stats.ykk_item_count, color: '#3B82F6' });
    if (stats.unitized_item_count > 0) pieData.push({ name: 'Unitized', value: stats.unitized_item_count, color: '#F59E0B' });
    if (stats.other_item_count > 0) pieData.push({ name: 'Other', value: stats.other_item_count, color: '#8B5CF6' });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="warehouse-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Warehouse Management</h1>
          <p className="text-muted-foreground">YKK Bhoruka and Project Stock Control</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={function() { setCatalogDialogOpen(true); }} data-testid="add-catalog-btn">
            <Plus size={16} className="mr-1" />Add Item
          </Button>
          <Button className="btn-accent" onClick={function() { setInwardDialogOpen(true); }} data-testid="inward-btn">
            <Scan size={16} className="mr-1" />Inward
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview"><BarChart3 size={16} className="mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="cutting"><FileSpreadsheet size={16} className="mr-1" />Cutting</TabsTrigger>
          <TabsTrigger value="inventory"><Package size={16} className="mr-1" />Inventory</TabsTrigger>
          <TabsTrigger value="remnants"><Scissors size={16} className="mr-1" />Remnants</TabsTrigger>
          <TabsTrigger value="dispatch"><Truck size={16} className="mr-1" />Dispatch</TabsTrigger>
          <TabsTrigger value="catalog"><Warehouse size={16} className="mr-1" />Catalog</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-6">
          <OverviewStats stats={stats} />
          <div className="grid md:grid-cols-2 gap-6">
            <StockChart pieData={pieData} />
            <AlertsCard alerts={stats?.low_stock_alerts} />
          </div>
        </TabsContent>

        <TabsContent value="cutting" className="mt-4">
          <CuttingTab 
            cuttingFile={cuttingFile}
            setCuttingFile={setCuttingFile}
            cuttingSettings={cuttingSettings}
            setCuttingSettings={setCuttingSettings}
            cuttingResults={cuttingResults}
            cuttingProcessing={cuttingProcessing}
            handleCuttingUpload={handleCuttingUpload}
            cuttingHistory={cuttingHistory}
            jobs={jobs}
          />
        </TabsContent>

        <TabsContent value="inventory" className="mt-4">
          <InventoryTab 
            inventory={filteredInventory} 
            search={search} 
            setSearch={setSearch}
            systemFilter={systemFilter}
            setSystemFilter={setSystemFilter}
            showQR={showQR}
            onConsume={handleConsumeInventory}
          />
        </TabsContent>

        <TabsContent value="remnants" className="mt-4">
          <RemnantsTab remnants={remnants} showQR={showQR} onConsume={handleConsumeRemnant} />
        </TabsContent>

        <TabsContent value="dispatch" className="mt-4">
          <DispatchTab stillages={stillages} onDispatch={handleDispatch} />
        </TabsContent>

        <TabsContent value="catalog" className="mt-4">
          <CatalogTab catalog={catalog} showQR={showQR} />
        </TabsContent>
      </Tabs>

      <CatalogDialog 
        open={catalogDialogOpen} 
        onOpenChange={setCatalogDialogOpen}
        form={catalogForm}
        setForm={setCatalogForm}
        onSubmit={handleCreateCatalog}
      />

      <InwardDialog
        open={inwardDialogOpen}
        onOpenChange={setInwardDialogOpen}
        form={inwardForm}
        setForm={setInwardForm}
        catalog={catalog}
        master={locationMaster}
        onSubmit={handleCreateInward}
      />

      <QRDialog open={qrDialogOpen} onOpenChange={setQRDialogOpen} qr={selectedQR} />
    </div>
  );
}

export default WarehousePage;
